import { useRef } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CaretRight } from "@phosphor-icons/react";
import { pageQueryOptions } from "@/api-queries/pages";
import { BlogRenderer } from "@/components/blog/BlogRenderer";
import { PageTableOfContents } from "@/components/pages/PageTableOfContents";
import { useTableOfContents } from "@/hooks/useTableOfContents";
import { Img } from "@/components/ui/img";
import { LOCALE_MAP } from "@/lib/locale";
import { formatDate } from "@/lib/format";
import "../../components/blog/editor.css";

const PAGE_BG = "oklch(98% 0.005 84)";

export const Route = createFileRoute("/pages/$slug")({
  component: PageView,
});

function PageView() {
  const { slug } = Route.useParams();
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language;

  const { data: activePage, isLoading } = useQuery(pageQueryOptions(slug, currentLocale));

  const contentRef = useRef<HTMLDivElement>(null);
  const { headings, activeId } = useTableOfContents(contentRef, activePage?.id);

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ background: PAGE_BG }}>
        <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-16">
          <div className="rounded-3xl bg-white px-5 py-10 shadow-[0_12px_40px_rgba(0,0,0,.05)] sm:px-12 sm:py-14">
            <div className="animate-pulse space-y-6">
              <div className="mx-auto h-4 w-48 rounded bg-muted" />
              <div className="mx-auto h-10 w-3/4 rounded bg-muted" />
              <div className="mx-auto h-px w-24 bg-muted" />
              <div className="space-y-3 pt-4">
                <div className="h-4 w-full rounded bg-muted" />
                <div className="h-4 w-full rounded bg-muted" />
                <div className="h-4 w-3/4 rounded bg-muted" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!activePage) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ background: PAGE_BG }}
      >
        <div className="text-center">
          <h1
            className="text-2xl font-bold text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {t("page.notFound")}
          </h1>
          <p className="mt-2 text-muted-foreground">{t("page.notFoundDesc")}</p>
          <Link
            to="/"
            className="mt-4 inline-block text-sm font-medium text-primary hover:underline"
          >
            {t("page.backHome")}
          </Link>
        </div>
      </div>
    );
  }

  const translations = (activePage.translations || []) as {
    id: string;
    locale: string;
    slug: string;
    status: string;
  }[];
  const publishedTranslations = translations.filter((t) => t.status === "published");
  const updatedAt = activePage.updatedAt as string | undefined;

  return (
    <div className="min-h-screen" style={{ background: PAGE_BG }}>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-16">
        <div className="lg:grid lg:grid-cols-[1fr_minmax(0,52rem)_1fr] lg:gap-10">
          {/* left gutter — balances the TOC so the sheet stays centered */}
          <div aria-hidden className="hidden lg:block" />

          {/* white article sheet */}
          <article className="mx-auto w-full max-w-3xl rounded-3xl bg-white px-5 py-10 shadow-[0_12px_40px_rgba(0,0,0,.05)] sm:px-12 sm:py-14 lg:mx-0 lg:max-w-none">
            <header className="text-center">
              <nav className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                <Link to="/" className="transition-colors hover:text-foreground">
                  {t("page.breadcrumbHome")}
                </Link>
                <CaretRight className="size-3" />
                <span className="max-w-[220px] truncate text-foreground/70">
                  {activePage.title}
                </span>
              </nav>

              {publishedTranslations.length > 0 && (
                <div className="mt-4 flex items-center justify-center gap-1.5">
                  {publishedTranslations.map((tr) => {
                    const lInfo = LOCALE_MAP[tr.locale];
                    return (
                      <Link
                        key={tr.id}
                        to={`/pages/${tr.slug}`}
                        className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        {lInfo?.flag} {lInfo?.label}
                      </Link>
                    );
                  })}
                </div>
              )}

              <h1
                className="mt-6 text-3xl font-bold leading-tight text-foreground sm:text-4xl"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {activePage.title}
              </h1>

              {updatedAt && (
                <p className="mt-3 text-sm text-muted-foreground">
                  {t("page.updated", { date: formatDate(updatedAt) })}
                </p>
              )}

              <div className="mx-auto mt-7 h-px w-16 bg-border" />
            </header>

            {activePage.coverImageUrl && (
              <div className="mt-8 overflow-hidden rounded-xl">
                <Img
                  src={activePage.coverImageUrl}
                  alt={activePage.title}
                  className="w-full object-cover"
                />
              </div>
            )}

            <div ref={contentRef} className="mt-8">
              <BlogRenderer content={activePage.content} className="ProseMirror" />
            </div>
          </article>

          {/* right gutter — sticky table of contents (desktop only) */}
          <div className="hidden lg:block">
            <PageTableOfContents headings={headings} activeId={activeId} />
          </div>
        </div>
      </div>
    </div>
  );
}
